import { useState, useEffect } from 'react';
import { Layers, ChevronDown, ChevronRight, Check, X, TrendingUp, TrendingDown, Minus, ArrowUpRight, ArrowDownRight, Archive } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import ConvictionSparkline, { computeTrajectory } from '../components/ConvictionSparkline.jsx';
import YourExposurePanel from '../components/YourExposurePanel.jsx';
import { usePage } from '../contexts/PageContext.jsx';

const STANCE_COLORS = { bullish: '#10b981', bearish: '#ef4444', neutral: '#6b7280' };
const STANCE_ICONS = { bullish: TrendingUp, bearish: TrendingDown, neutral: Minus };
const STATUS_COLORS = { active: '#10b981', dormant: '#6b7280', invalidated: '#ef4444' };

function trajectoryColor(stance, direction) {
  // Green = thesis strengthening: rising bullish OR falling bearish
  if (direction === 'stable') return '#6b7280';
  if (stance === 'bullish' && direction === 'up') return '#10b981';
  if (stance === 'bearish' && direction === 'down') return '#10b981';
  if (stance === 'bullish' && direction === 'down') return '#ef4444';
  if (stance === 'bearish' && direction === 'up') return '#ef4444';
  return '#6b7280';
}

function TrajectoryBadge({ history, stance }) {
  const traj = computeTrajectory(history);
  if (!traj) return <span className="text-[9px] font-mono text-korinth-muted">—</span>;
  const color = trajectoryColor(stance, traj.direction);
  const arrow = traj.direction === 'up' ? '▲' : traj.direction === 'down' ? '▼' : '→';
  const sign = traj.delta >= 0 ? '+' : '';
  return (
    <span className="text-[10px] font-mono" style={{ color }}>
      {arrow} {sign}{traj.delta.toFixed(1)} over {traj.days}d
    </span>
  );
}

function ConvictionBar({ value, stance }) {
  const pct = Math.min(100, (value || 0) * 10);
  const color = STANCE_COLORS[stance] || '#6b7280';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-korinth-bg rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-mono font-bold text-white w-10 text-right">{(value || 0).toFixed(1)}</span>
    </div>
  );
}

function PerformanceStats({ themeId }) {
  const [perf, setPerf] = useState(null);
  useEffect(() => {
    fetch(`/api/themes/${themeId}/performance`).then(r => r.ok ? r.json() : null).then(setPerf);
  }, [themeId]);

  if (!perf) return <div className="text-[10px] text-korinth-muted font-mono">Loading…</div>;
  if (perf.collecting_data) {
    return <div className="text-[10px] text-korinth-muted font-mono italic">Collecting data — {perf.days_active}d active</div>;
  }
  if (perf.trades_count === 0) {
    return <div className="text-[10px] text-korinth-muted font-mono italic">No theme-aligned shadow trades yet ({perf.days_active}d active)</div>;
  }

  const hitColor = perf.hit_rate >= 60 ? '#10b981' : perf.hit_rate >= 40 ? '#f59e0b' : '#ef4444';
  const pnlColor = perf.pnl_usd >= 0 ? '#10b981' : '#ef4444';

  return (
    <div className="space-y-0.5 text-[10px] font-mono">
      <div>
        Hit rate: <span style={{ color: hitColor }} className="font-bold">{perf.hit_rate}%</span>
        <span className="text-korinth-muted"> ({perf.winners}/{perf.trades_count})</span>
      </div>
      <div>
        P&amp;L: <span style={{ color: pnlColor }} className="font-bold">{perf.pnl_usd >= 0 ? '+' : ''}${perf.pnl_usd.toLocaleString()}</span>
        <span className="text-korinth-muted"> shadow · {perf.days_active}d</span>
      </div>
      {perf.best_call && (
        <div className="text-korinth-muted">
          Best: <span className="text-korinth-accent">{perf.best_call.ticker || perf.best_call.asset} {perf.best_call.direction} +${perf.best_call.pnl_usd}</span>
        </div>
      )}
      {perf.worst_call && perf.worst_call.pnl_usd < 0 && (
        <div className="text-korinth-muted">
          Worst: <span className="text-korinth-danger">{perf.worst_call.ticker || perf.worst_call.asset} {perf.worst_call.direction} ${perf.worst_call.pnl_usd}</span>
        </div>
      )}
    </div>
  );
}

function ActiveCallsSection({ themeId }) {
  const [calls, setCalls] = useState(null);
  useEffect(() => {
    fetch(`/api/themes/${themeId}/active-calls`).then(r => r.ok ? r.json() : null).then(setCalls);
  }, [themeId]);

  if (!calls) return null;
  const { parent_predictions = [], active_recommendations = [] } = calls;

  return (
    <div className="space-y-2">
      <div>
        <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1">
          Active KAIROS calls supporting this theme ({parent_predictions.length})
        </div>
        {parent_predictions.length > 0 ? (
          <div className="space-y-0.5">
            {parent_predictions.map(p => {
              const days = Math.round((Date.now() - new Date(p.first_created_at).getTime()) / 86400000);
              return (
                <a key={p.id} href="/history" className="flex items-center gap-2 px-2 py-1 bg-korinth-bg rounded text-[10px] font-mono hover:border-korinth-accent/30 border border-transparent">
                  <span className={p.direction === 'bullish' ? 'text-korinth-accent' : 'text-korinth-danger'}>
                    {p.direction === 'bullish' ? '🟢 ▲' : '🔴 ▼'}
                  </span>
                  <span className="text-white font-bold w-20 truncate">{p.asset}</span>
                  <span className="text-[9px]">{p.direction} {p.timeframe}</span>
                  <span className="text-korinth-muted">@ {p.confidence}%</span>
                  {p.target_price && <span className="text-korinth-muted">target {p.target_price}</span>}
                  <span className="text-[9px] text-korinth-muted ml-auto">{days}d open</span>
                  {p.update_count > 0 && <span className="text-[8px] px-1 rounded bg-purple-500/10 text-purple-400">×{p.update_count}</span>}
                </a>
              );
            })}
          </div>
        ) : <p className="text-[10px] text-korinth-muted italic">No active parent predictions</p>}
      </div>

      <div>
        <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1">
          Active recommendations to act on ({active_recommendations.length})
        </div>
        {active_recommendations.length > 0 ? (
          <div className="space-y-0.5">
            {active_recommendations.map(r => (
              <a key={r.id} href="/portfolio-command" className="flex items-center gap-2 px-2 py-1 bg-korinth-bg rounded text-[10px] font-mono hover:border-korinth-accent/30 border border-transparent">
                <span className={r.direction === 'buy' ? 'text-korinth-accent' : 'text-korinth-danger'}>{r.direction.toUpperCase()}</span>
                <span className="text-white font-bold w-20 truncate">{r.asset}</span>
                <span className="text-korinth-accent">{r.conviction_overall?.toFixed(1)}/10</span>
                {r.position_size_recommended_pct && <span className="text-korinth-muted">size {r.position_size_recommended_pct}%</span>}
                {r.entry_strategy && <span className="text-[9px] text-korinth-muted ml-auto">{String(r.entry_strategy).slice(0, 30)}</span>}
              </a>
            ))}
          </div>
        ) : <p className="text-[10px] text-korinth-muted italic">No active recommendations yet — KAIROS will generate them on next cycle if conviction crosses threshold.</p>}
      </div>
    </div>
  );
}

function DormantHint({ themeId }) {
  const [hint, setHint] = useState(null);
  useEffect(() => {
    fetch(`/api/themes/${themeId}/activation-hint`).then(r => r.ok ? r.json() : null).then(setHint);
  }, [themeId]);

  if (!hint) return null;
  return (
    <div className="text-[10px] font-mono text-korinth-muted space-y-1">
      <p>{hint.hint}</p>
      {hint.current_watchlist_signals?.length > 0 && (
        <div>
          Current watchlist signals on these tickers:
          <span className="ml-1">
            {hint.current_watchlist_signals.map(s => `${s.ticker}:${s.signal_strength || 0}`).join(', ')}
          </span>
        </div>
      )}
    </div>
  );
}

function ThemeCard({ theme, expanded, onToggle, onReload, compact = false }) {
  const Icon = STANCE_ICONS[theme.current_stance] || Minus;
  const color = STANCE_COLORS[theme.current_stance] || '#6b7280';
  const statusColor = STATUS_COLORS[theme.status] || '#6b7280';

  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (expanded && !detail) {
      fetch(`/api/themes/${theme.id}`).then(r => r.ok ? r.json() : null).then(setDetail);
    }
  }, [expanded, theme.id]);

  const daysSinceUpdate = theme.last_updated_at ? Math.round((Date.now() - new Date(theme.last_updated_at).getTime()) / 86400000) : 0;

  return (
    <div className={`bg-korinth-surface border border-korinth-border rounded-lg ${theme.status === 'dormant' ? 'opacity-75' : ''} ${theme.status === 'invalidated' ? 'opacity-50' : ''}`}>
      <button onClick={onToggle} className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-korinth-bg/30 transition-colors">
        {expanded ? <ChevronDown className="w-4 h-4 text-korinth-muted mt-0.5" /> : <ChevronRight className="w-4 h-4 text-korinth-muted mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-mono font-bold text-white">{theme.name}</span>
            <Icon className="w-3.5 h-3.5" style={{ color }} />
            <span className="text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded" style={{ color, backgroundColor: color + '15' }}>{theme.current_stance}</span>
            {theme.status !== 'active' && (
              <span className="text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded" style={{ color: statusColor, backgroundColor: statusColor + '15' }}>{theme.status}</span>
            )}
            <span className="text-[9px] text-korinth-muted">{theme.category}</span>
            <ConvictionSparkline data={theme.conviction_history} stance={theme.current_stance} width={50} height={16} />
          </div>
          <div className="mt-2">
            <ConvictionBar value={theme.current_conviction} stance={theme.current_stance} />
          </div>
          <div className="flex gap-3 mt-1.5 text-[10px] font-mono text-korinth-muted items-center flex-wrap">
            <TrajectoryBadge history={theme.conviction_history} stance={theme.current_stance} />
            <span>·</span>
            <span>{theme.parent_count} parents</span>
            <span>·</span>
            <span>{theme.ticker_count} tickers</span>
            <span>·</span>
            <span>{daysSinceUpdate}d since update</span>
          </div>
        </div>
      </button>

      {expanded && detail && (
        <div className="px-4 pb-3 border-t border-korinth-border/30 pt-3 space-y-4">
          {detail.description && <p className="text-[11px] font-mono text-korinth-text">{detail.description}</p>}
          {detail.current_reasoning && <p className="text-[10px] font-mono text-korinth-accent">{detail.current_reasoning}</p>}

          {/* Dormant activation hint */}
          {theme.status === 'dormant' && (
            <div className="bg-korinth-bg rounded p-2 border border-korinth-border/30">
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1">Activation hint</div>
              <DormantHint themeId={theme.id} />
            </div>
          )}

          {/* Active calls */}
          {theme.status === 'active' && <ActiveCallsSection themeId={theme.id} />}

          {/* Your Exposure */}
          <div>
            <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1">Your Exposure</div>
            <YourExposurePanel
              tickers={detail.ticker_links?.map(t => ({ ticker: t.ticker, direction: t.direction, link_weight: t.link_weight })) || []}
              maxVisible={5}
            />
          </div>

          {/* Linked tickers with contribution */}
          {detail.ticker_links?.length > 0 && (
            <div>
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1">Linked Tickers ({detail.ticker_links.length})</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                {detail.ticker_links.map(t => {
                  const contribColor = t.direction === 'benefits_from' ? '#10b981' : '#ef4444';
                  const contribPts = ((theme.current_conviction || 0) * (t.link_weight || 1.0) * 3 * (t.direction === 'benefits_from' ? 1 : -1)).toFixed(1);
                  return (
                    <a key={t.link_id} href="/watchlist" className="flex items-center gap-1.5 px-2 py-1 bg-korinth-bg rounded text-[10px] font-mono hover:border-korinth-accent/30 border border-transparent">
                      <span className="text-white font-bold w-12">{t.ticker}</span>
                      <span className="text-[8px] text-korinth-muted">{t.direction === 'benefits_from' ? 'benefits' : 'hurt by'}</span>
                      <span className="text-[8px] text-korinth-muted">×{t.link_weight}</span>
                      <span className="ml-auto font-bold text-[10px]" style={{ color: contribColor }}>
                        {contribPts > 0 ? '+' : ''}{contribPts}
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {/* Performance */}
          {theme.status !== 'invalidated' && (
            <div>
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1">Theme performance</div>
              <PerformanceStats themeId={theme.id} />
            </div>
          )}

          {/* History */}
          {detail.conviction_history?.length > 0 && (
            <div>
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1">Recent History</div>
              <div className="space-y-0.5">
                {detail.conviction_history.slice(-5).reverse().map((h, i) => (
                  <div key={i} className="text-[9px] font-mono text-korinth-muted flex gap-2">
                    <span className="text-korinth-accent w-8">{(h.value || 0).toFixed(1)}</span>
                    <span className="w-16">{h.stance}</span>
                    <span className="w-28">{h.computed_at?.slice(5, 16)}</span>
                    <span className="flex-1 truncate">{h.reasoning}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {theme.status !== 'invalidated' && (
            <div className="flex gap-2 pt-2 border-t border-korinth-border/20">
              <button onClick={async () => {
                const reason = prompt('Invalidation reason?'); if (!reason) return;
                await fetch(`/api/themes/${theme.id}/invalidate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) });
                onReload();
              }} className="text-[10px] font-mono px-2 py-1 rounded bg-korinth-danger/10 text-korinth-danger border border-korinth-danger/30 hover:bg-korinth-danger/20">
                Invalidate
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryBar({ summary }) {
  if (!summary) return null;
  const top = summary.top_conviction;
  const mover = summary.biggest_mover_24h;

  return (
    <div className="bg-korinth-surface border border-korinth-border rounded-lg p-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm font-mono">
        <div>
          <div className="text-[9px] text-korinth-muted uppercase tracking-wider mb-1">Most conviction</div>
          {top ? (
            <div>
              <span className="font-bold text-white">{top.name}</span>
              <span className="ml-2 text-lg font-mono font-bold" style={{ color: STANCE_COLORS[top.stance] }}>{(top.conviction || 0).toFixed(1)}</span>
              <span className="text-[10px] text-korinth-muted ml-2">{top.category} · {top.stance}</span>
            </div>
          ) : <span className="text-korinth-muted">No active themes</span>}
        </div>

        <div>
          <div className="text-[9px] text-korinth-muted uppercase tracking-wider mb-1">Biggest 24h mover</div>
          {mover ? (
            <div>
              <span className="font-bold text-white">{mover.name}</span>
              <span className={`ml-2 ${mover.direction === 'up' ? 'text-korinth-accent' : 'text-korinth-danger'}`}>
                {mover.direction === 'up' ? <ArrowUpRight className="w-3 h-3 inline" /> : <ArrowDownRight className="w-3 h-3 inline" />}
                {(mover.from || 0).toFixed(1)} → {(mover.to || 0).toFixed(1)}
              </span>
            </div>
          ) : <span className="text-korinth-muted">No changes in last 24h</span>}
        </div>

        <div>
          <div className="text-[9px] text-korinth-muted uppercase tracking-wider mb-1">Awaiting activation</div>
          <span className="font-bold text-white">{summary.dormant_count}</span>
          <span className="text-korinth-muted"> dormant themes</span>
        </div>
      </div>
    </div>
  );
}

function CandidatesTab({ onPromoteReload }) {
  const [candidates, setCandidates] = useState([]);

  async function load() {
    const r = await fetch('/api/themes/candidates/pending');
    if (r.ok) setCandidates(await r.json());
  }
  useEffect(() => { load(); }, []);

  async function promote(id) {
    await fetch(`/api/themes/candidates/${id}/promote`, { method: 'POST' });
    load(); onPromoteReload();
  }
  async function reject(id) {
    await fetch(`/api/themes/candidates/${id}/reject`, { method: 'POST' });
    load();
  }

  if (candidates.length === 0) {
    return <p className="text-[10px] text-korinth-muted font-mono text-center py-6">No pending theme candidates</p>;
  }

  return (
    <div className="space-y-2">
      {candidates.map(c => (
        <div key={c.id} className="bg-korinth-surface border border-korinth-warning/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-mono font-bold text-white">{c.proposed_name}</span>
            <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-korinth-border text-korinth-text">{c.proposed_category}</span>
            {c.proposed_stance && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded" style={{ color: STANCE_COLORS[c.proposed_stance], backgroundColor: (STANCE_COLORS[c.proposed_stance] || '#6b7280') + '15' }}>{c.proposed_stance}</span>}
          </div>
          {c.proposed_description && <p className="text-[11px] font-mono text-korinth-text mb-2">{c.proposed_description}</p>}
          {c.agent_reasoning && <p className="text-[10px] font-mono text-korinth-accent mb-2">{c.agent_reasoning}</p>}
          {c.proposed_macro_link_ids?.length > 0 && (
            <div className="text-[9px] font-mono text-korinth-muted mb-1">Proposed parent predictions: {c.proposed_macro_link_ids.join(', ')}</div>
          )}
          {c.proposed_ticker_links?.length > 0 && (
            <div className="text-[9px] font-mono text-korinth-muted mb-2">Proposed tickers: {c.proposed_ticker_links.map(t => t.ticker).join(', ')}</div>
          )}
          <div className="flex gap-2">
            <button onClick={() => promote(c.id)} className="flex items-center gap-1 text-[10px] font-mono px-3 py-1 rounded bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30 hover:bg-korinth-accent/20">
              <Check className="w-3 h-3" /> Promote
            </button>
            <button onClick={() => reject(c.id)} className="flex items-center gap-1 text-[10px] font-mono px-3 py-1 rounded bg-korinth-danger/10 text-korinth-danger border border-korinth-danger/30 hover:bg-korinth-danger/20">
              <X className="w-3 h-3" /> Reject
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ThemesPage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('themes'); }, []);

  const [tab, setTab] = useState('themes');
  const [data, setData] = useState({ themes: [], summary: {}, candidates_pending: 0 });
  const [summary, setSummary] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [dormantExpanded, setDormantExpanded] = useState(false);
  const [showInvalidated, setShowInvalidated] = useState(false);

  async function load() {
    try {
      const [r1, r2] = await Promise.all([
        fetch('/api/themes'),
        fetch('/api/themes/summary/overview'),
      ]);
      if (r1.ok) setData(await r1.json());
      if (r2.ok) setSummary(await r2.json());
    } catch {}
  }
  useEffect(() => { load(); const iv = setInterval(load, 120000); return () => clearInterval(iv); }, []);

  // Split into active/dormant/invalidated
  const active = data.themes.filter(t => t.status === 'active').sort((a, b) => (b.current_conviction || 0) - (a.current_conviction || 0));
  const dormant = data.themes.filter(t => t.status === 'dormant');
  const invalidated = data.themes.filter(t => t.status === 'invalidated');

  // Group active by category
  const activeGrouped = active.reduce((acc, t) => {
    const key = t.category || 'uncategorized';
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Layers className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Themes</h1>
        <span className="text-korinth-muted text-sm font-mono">CLOTHO · Theme Weaver — the reasoning hierarchy</span>
      </div>

      {tab === 'themes' && <SummaryBar summary={summary} />}

      <div className="flex items-center gap-4 text-xs font-mono">
        <span className="text-korinth-accent">{data.summary.active || 0} active</span>
        <span className="text-korinth-muted">·</span>
        <span className="text-korinth-muted">{data.summary.dormant || 0} dormant</span>
        <span className="text-korinth-muted">·</span>
        <span className="text-korinth-danger">{data.summary.invalidated || 0} invalidated</span>
      </div>

      <div className="flex gap-1">
        <button onClick={() => setTab('themes')}
          className={`px-3 py-2 rounded text-xs font-mono font-bold ${tab === 'themes' ? 'bg-korinth-accent/10 text-korinth-accent border border-korinth-accent' : 'text-korinth-muted border border-transparent hover:bg-korinth-border/50'}`}>
          Themes
        </button>
        <button onClick={() => setTab('candidates')}
          className={`px-3 py-2 rounded text-xs font-mono font-bold ${tab === 'candidates' ? 'bg-korinth-accent/10 text-korinth-accent border border-korinth-accent' : 'text-korinth-muted border border-transparent hover:bg-korinth-border/50'}`}>
          Candidates
          {data.candidates_pending > 0 && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-korinth-warning text-black text-[9px]">{data.candidates_pending}</span>}
        </button>
      </div>

      {tab === 'themes' && (
        <div className="space-y-6">
          {/* Active themes — 2-column grid per category */}
          {Object.entries(activeGrouped).map(([category, themes]) => (
            <ModuleContainer key={category} title={category.charAt(0).toUpperCase() + category.slice(1)} subtitle={`${themes.length} active`}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {themes.map(t => (
                  <ThemeCard key={t.id} theme={t} expanded={expanded === t.id} onToggle={() => setExpanded(expanded === t.id ? null : t.id)} onReload={load} />
                ))}
              </div>
            </ModuleContainer>
          ))}

          {/* Dormant accordion */}
          {dormant.length > 0 && (
            <div className="bg-korinth-surface border border-korinth-border rounded-lg">
              <button onClick={() => setDormantExpanded(!dormantExpanded)}
                className="w-full px-4 py-3 flex items-center gap-2 text-left hover:bg-korinth-bg/30">
                {dormantExpanded ? <ChevronDown className="w-4 h-4 text-korinth-muted" /> : <ChevronRight className="w-4 h-4 text-korinth-muted" />}
                <Archive className="w-3.5 h-3.5 text-korinth-muted" />
                <span className="text-sm font-mono font-bold text-white">Dormant ({dormant.length})</span>
                <span className="text-[10px] text-korinth-muted">Awaiting activation from matching predictions</span>
              </button>
              {dormantExpanded && (
                <div className="px-4 pb-3 pt-1 border-t border-korinth-border/30">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
                    {dormant.map(t => (
                      <ThemeCard key={t.id} theme={t} expanded={expanded === t.id} onToggle={() => setExpanded(expanded === t.id ? null : t.id)} onReload={load} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Invalidated toggle */}
          {invalidated.length > 0 && (
            <div>
              <button onClick={() => setShowInvalidated(!showInvalidated)}
                className="text-[10px] font-mono text-korinth-muted hover:text-korinth-accent">
                {showInvalidated ? 'Hide' : 'Show'} invalidated themes ({invalidated.length})
              </button>
              {showInvalidated && (
                <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {invalidated.map(t => (
                    <ThemeCard key={t.id} theme={t} expanded={expanded === t.id} onToggle={() => setExpanded(expanded === t.id ? null : t.id)} onReload={load} />
                  ))}
                </div>
              )}
            </div>
          )}

          {data.themes.length === 0 && (
            <p className="text-[10px] text-korinth-muted font-mono text-center py-8">
              No themes seeded yet. Run: <code className="text-korinth-accent">node backend/scripts/sprint17_seed_themes.js --apply</code>
            </p>
          )}
        </div>
      )}

      {tab === 'candidates' && (
        <ModuleContainer title="Pending Candidates" subtitle="Agent-proposed themes awaiting manual review">
          <CandidatesTab onPromoteReload={load} />
        </ModuleContainer>
      )}
    </div>
  );
}
